import { useCallback, useEffect, useMemo, useState } from "react";
import type { Refs, Row } from "../types";
import { api, ApiError, type Observacao } from "../api";
import ErrorState from "./ErrorState";
import { IconClose } from "../icons";
import { ordenarPorNome } from "../ordem";

interface Props {
  refs: Refs;
  /** Colaborador vinculado ao login — mostra só a escala dele ("Minha Agenda"). */
  colaboradorId?: number | null;
}

/** Paleta de cores das células (mesmas do cronograma da SERCAMP). */
export const AGENDA_CORES: { key: string; label: string }[] = [
  { key: "preagendado", label: "Pré-agendado (aguardando cliente)" },
  { key: "confirmado", label: "Confirmado com cliente" },
  { key: "pessego", label: "Treinamento" },
  { key: "lavanda", label: "Externo (azul)" },
  { key: "rosa", label: "Externo (rosa)" },
  { key: "rosa-forte", label: "Integração" },
  { key: "verde", label: "Externo (verde)" },
  { key: "marrom", label: "Externo (marrom)" },
  { key: "laranja", label: "Externo (laranja)" },
  { key: "ciano", label: "Férias" },
  { key: "azul", label: "Fechado" },
  { key: "amarelo", label: "Folga" },
  { key: "branco", label: "Interno / Sercamp" },
];

/* ----------------------------- Helpers de data ----------------------------- */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
/** Segunda-feira da semana da data informada. */
function startOfWeekMonday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7; // segunda = 0
  x.setDate(x.getDate() - dow);
  return x;
}

const fmtWeekday = new Intl.DateTimeFormat("pt-BR", { weekday: "long" });
const fmtDay = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
const fmtRange = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" });
/** Carimbo curto do recado ("03/09/26 14:20") — cabe na largura do celular. */
const fmtCarimbo = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
});
/** O que cada cor quer dizer (para o popup mostrar por extenso). */
const ROTULO_COR = new Map(AGENDA_CORES.map((c) => [c.key, c.label]));

/**
 * Agenda em modo SOMENTE LEITURA (Operacional). O cronograma é montado no
 * Planejamento (mesma base `sercamp_planejamento`); aqui a equipe apenas
 * CONSULTA a agenda — sem criar, editar ou excluir alocações.
 */
export default function AgendaView({ refs, colaboradorId = null }: Props) {
  const [start, setStart] = useState<Date>(() => startOfWeekMonday(new Date()));
  const [span, setSpan] = useState<number>(7);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  /**
   * O dia que a pessoa abriu para ler (duplo clique na célula, ou toque no
   * número vermelho). A agenda continua SOMENTE LEITURA: este popup mostra o
   * serviço e os recados do planejador — não edita nada.
   */
  const [aberta, setAberta] = useState<{ colab: Row; date: string; entry: Row } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list("agenda"));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(
    () => Array.from({ length: span }, (_, i) => addDays(start, i)),
    [start, span],
  );

  const map = useMemo(() => {
    const m = new Map<string, Row>();
    for (const r of rows) {
      const d = String(r.data).slice(0, 10);
      m.set(`${r.colaborador_id}|${d}`, r);
    }
    return m;
  }, [rows]);

  const today = ymd(new Date());
  // "Minha Agenda": mostra SÓ a linha do colaborador vinculado ao login. Se o
  // login não tiver colaborador (ex.: admin), mostra todos — em ordem
  // alfabética, igual à agenda do Planejamento (o cadastro chega por id).
  const colaboradores = ordenarPorNome(
    colaboradorId == null
      ? refs.colaboradores
      : refs.colaboradores.filter((c) => Number(c.id) === Number(colaboradorId)),
    (c) => c.nome,
  );

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  const periodo = `${fmtRange.format(days[0])} a ${fmtRange.format(days[days.length - 1])}`;

  return (
    <div className="agenda agenda-readonly">
      <div className="agenda-banner">CRONOGRAMA DE SERVIÇOS INTERNOS E EXTERNOS</div>

      <div className="agenda-toolbar">
        <div className="agenda-nav">
          <button className="btn-secondary" onClick={() => setStart((s) => addDays(s, -span))}>
            ‹ Anterior
          </button>
          <button className="btn-secondary" onClick={() => setStart(startOfWeekMonday(new Date()))}>
            Hoje
          </button>
          <button className="btn-secondary" onClick={() => setStart((s) => addDays(s, span))}>
            Próximo ›
          </button>
          <span className="agenda-periodo">{periodo}</span>
        </div>
        <div className="agenda-span">
          <label>
            Período:&nbsp;
            <select value={span} onChange={(e) => setSpan(Number(e.target.value))}>
              <option value={7}>1 semana</option>
              <option value={11}>11 dias</option>
              <option value={14}>2 semanas</option>
            </select>
          </label>
        </div>
      </div>

      <div className="agenda-legend">
        {AGENDA_CORES.map((c) => (
          <span key={c.key} className="agenda-legend-item">
            <span className={`agenda-swatch ag-${c.key}`} />
            {c.label}
          </span>
        ))}
      </div>

      <div className="agenda-grid-wrap">
        {loading ? (
          <div className="empty">Carregando agenda...</div>
        ) : colaboradores.length === 0 ? (
          <div className="empty">
            Nenhuma escala encontrada. Se seu login não está vinculado a um colaborador, peça ao
            administrador para vincular no cadastro de usuários.
          </div>
        ) : (
          <table className="agenda-grid">
            <thead>
              <tr>
                <th className="ag-corner">COLABORADORES</th>
                {days.map((d) => {
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th
                      key={ymd(d)}
                      className={`ag-head${weekend ? " ag-weekend" : ""}${ymd(d) === today ? " ag-today" : ""}`}
                    >
                      <span className="ag-head-dow">{fmtWeekday.format(d)}</span>
                      <span className="ag-head-date">{fmtDay.format(d)}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {colaboradores.map((colab) => (
                <tr key={colab.id}>
                  <th className="ag-name">{colab.nome}</th>
                  {days.map((d) => {
                    const key = `${colab.id}|${ymd(d)}`;
                    const entry = map.get(key) ?? null;
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <td
                        key={key}
                        className={`ag-cell${weekend ? " ag-weekend" : ""}${
                          entry ? " ag-cell-abrivel" : ""
                        }`}
                        /* DOIS CLIQUES ABREM O DIA. Um clique só continua não
                           fazendo nada: rolar a grade no celular encostando na
                           célula abriria popup o tempo todo. */
                        onDoubleClick={
                          entry ? () => setAberta({ colab, date: ymd(d), entry }) : undefined
                        }
                        title={entry ? "Clique duas vezes para ver os detalhes" : undefined}
                      >
                        {entry ? (
                          (() => {
                            const obs = Number(entry.observacoes ?? 0);
                            return (
                              <span className={`ag-block ag-${entry.cor}`}>
                                {entry.servico}
                                {/* O número vermelho de "tem recado", igual ao
                                    badge de notificação do celular. Ele também
                                    ABRE o dia num toque só: duplo toque em tela
                                    de celular é o gesto de dar zoom, e quem só
                                    quer ler o recado não deveria brigar com
                                    isso. */}
                                {obs > 0 && (
                                  <span
                                    className="ag-obs-badge ag-obs-badge-toque"
                                    role="button"
                                    tabIndex={0}
                                    title={obs === 1 ? "1 observação" : `${obs} observações`}
                                    aria-label={
                                      obs === 1
                                        ? "1 observação — abrir"
                                        : `${obs} observações — abrir`
                                    }
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      setAberta({ colab, date: ymd(d), entry });
                                    }}
                                    onKeyDown={(ev) => {
                                      if (ev.key === "Enter" || ev.key === " ") {
                                        ev.preventDefault();
                                        setAberta({ colab, date: ymd(d), entry });
                                      }
                                    }}
                                  >
                                    {obs}
                                  </span>
                                )}
                              </span>
                            );
                          })()
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {aberta && (
        <DiaDetalheModal
          colabNome={aberta.colab.nome}
          dateLabel={fmtDay.format(new Date(aberta.date + "T00:00:00"))}
          entry={aberta.entry}
          onClose={() => setAberta(null)}
        />
      )}
    </div>
  );
}


/* ------------------------- Popup de leitura do dia ------------------------- */
/**
 * O DIA ABERTO — o que a equipe precisa saber antes de sair.
 *
 * A grade mostra o serviço espremido num quadradinho de 11px; aqui cabe o
 * serviço inteiro, o que a cor quer dizer e, principalmente, OS RECADOS que o
 * planejador escreveu para aquele dia. É somente leitura: quem escreve é o
 * Planejamento (mesma base, ver server/agenda-observacoes.ts).
 *
 * As observações são buscadas ao ABRIR e não junto da grade: uma agenda de duas
 * semanas traria o texto de todos os dias de todo mundo para desenhar uma
 * bolinha vermelha. Do fetch da listagem vem só a CONTAGEM.
 *
 * Declarado no topo do módulo (e não dentro do AgendaView) porque componente
 * declarado dentro de outro é remontado a cada render do pai.
 */
function DiaDetalheModal({
  colabNome,
  dateLabel,
  entry,
  onClose,
}: {
  colabNome: string;
  dateLabel: string;
  entry: Row;
  onClose: () => void;
}) {
  const [lista, setLista] = useState<Observacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const agendaId = Number(entry.id);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    api
      .agendaObservacoes(agendaId)
      .then((rows) => {
        if (!vivo) return;
        setLista(rows);
        setErro(null);
      })
      .catch((err) => {
        if (!vivo) return;
        setErro(err instanceof Error ? err.message : "Não foi possível carregar as observações.");
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [agendaId]);

  // Cor da paleta tem nome ("Externo (azul)"); cor escolhida a dedo no
  // Planejamento chega como hexadecimal — mostrar "#7c3aed" para o colaborador
  // não diz nada, então ela vira só "Cor personalizada".
  const cor = String(entry.cor);
  const rotuloCor = ROTULO_COR.get(cor) ?? (/^#[0-9a-f]{6}$/i.test(cor) ? "Cor personalizada" : cor);
  const folga = entry.cor === "amarelo";

  return (
    <div
      className="modal-overlay"
      /*
       * FECHAR AO CLICAR FORA — mas NÃO com o segundo clique do gesto que
       * acabou de abrir. A bolinha vermelha abre num toque só; quem tem o
       * costume de dar dois toques via o popup abrir e fechar na mesma hora,
       * porque o segundo toque já caía aqui, no fundo. `detail` é a contagem
       * de cliques do gesto: 1 é um clique de verdade, 2 é a segunda batida
       * do duplo — e essa a gente ignora.
       */
      onMouseDown={(e) => {
        if (e.detail > 1) return;
        onClose();
      }}
    >
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Detalhes do dia</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="agenda-dia-info">
            <strong>{colabNome}</strong>
            <span className="muted">{dateLabel}</span>
          </div>

          <div className="agenda-dia-servico">
            <span className={`ag-block ag-${entry.cor}`}>
              {entry.servico || "(sem descrição)"}
            </span>
            <p className="muted">{rotuloCor}</p>
          </div>

          {/* A folga tem uma segunda vida na tela: o gestor pode ter reprovado,
              e o motivo dele é a informação mais importante do dia. */}
          {folga && entry.folga_status && (
            <p className="agenda-dia-folga">
              Folga <strong>{String(entry.folga_status)}</strong>
              {entry.folga_obs ? ` — ${String(entry.folga_obs)}` : ""}
            </p>
          )}

          <div className="agenda-obs">
            <div className="agenda-obs-head">
              <span>Observações do planejamento</span>
              {lista.length > 0 && <span className="agenda-obs-contador">{lista.length}</span>}
            </div>

            {carregando ? (
              <p className="muted">Carregando observações...</p>
            ) : erro ? (
              <div className="form-error">{erro}</div>
            ) : lista.length === 0 ? (
              <p className="muted">Nenhuma observação para este dia.</p>
            ) : (
              <div className="agenda-obs-lista">
                {lista.map((o) => (
                  <div className="agenda-obs-item" key={o.id}>
                    <p className="agenda-obs-texto">{o.texto}</p>
                    <div className="agenda-obs-rodape">
                      <span className="muted">
                        {o.autor ? `${o.autor} · ` : ""}
                        {fmtCarimbo.format(new Date(o.created_at))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
